import debounce from 'lodash/debounce';
import { between } from '../../utils/format';
import isServer from '../../utils/isServer';

const dragType = {
  MIN: 0,
  MAX: 1,
};

export default {
  name: 'Range',
  props: {
    value: {
      type: Object,
      default: () => ({
        min: null,
        max: null,
      }),
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: Number,
      default: 1,
    },
    lazy: Boolean,
    disable: Boolean,
    data: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      model: {
        min: this.value.min,
        max: this.value.max,
      },
      curMinRatio: 0,
      curMaxRatio: 1,
      active: false,
      preventFocus: false,
      dragg: false,
      dragType: null,
    };
  },
  computed: {
    ratioMin() {
      return this.active === true ? this.curMinRatio : this.modelMinRatio;
    },
    ratioMax() {
      return this.active === true ? this.curMaxRatio : this.modelMaxRatio;
    },
    // В случае, если установлен параметр data
    // min\maxValue указываются не в реальных значениях
    // а в индексах от массива data.
    // т.е для data = [1, 6, 12, 18]
    // значение числа 12 будет равно просто 2
    minValue() {
      return this.data == null ? this.min : 0;
    },
    maxValue() {
      return this.data == null ? this.max : this.data.length - 1;
    },

    modelMinRatio() {
      return (this.model.min - this.minValue) / (this.maxValue - this.minValue);
    },
    modelMaxRatio() {
      return (this.model.max - this.minValue) / (this.maxValue - this.minValue);
    },
    trackStyle() {
      return {
        left: `${100 * this.ratioMin}%`,
        width: `${100 * (this.ratioMax - this.ratioMin)}%`,
      };
    },
    minThumbStyle() {
      return {
        left: `${100 * this.ratioMin}%`,
        'z-index': this.__nextFocus === 'min' ? 2 : void 0,
      };
    },
    maxThumbStyle() {
      return {
        left: `${100 * this.ratioMax}%`,
      };
    },
    classes() {
      return (this.active === true ? ' range_active' : '') + (this.disable === true ? ' range_disable' : '');
    },
    dynamicTabIndex() {
      return this.$props.disable ? '-1' : '0';
    },
  },
  watch: {
    'value.min'(val, oldVal) {
      if (val === null) {
        this.model.min = this.minValue;
        return;
      }

      if (this.data == null) {
        this.model.min = val;
        return;
      }

      this.model.min = this.data.indexOf(val);
    },
    'value.max'(val) {
      if (val === null) {
        this.model.max = this.maxValue;
        return;
      }

      if (this.data === null) {
        this.model.max = val;
        return;
      }

      this.model.max = this.data.indexOf(val);
    },
    minValue(value) {
      if (this.model.min < value) {
        this.model.min = value;
      }
      if (this.model.max < value) {
        this.model.max = value;
      }
    },
    maxValue(value) {
      if (this.model.min > value) {
        this.model.min = value;
      }
      if (this.model.max > value) {
        this.model.max = value;
      }
    },
  },
  mounted() {
    if (this.data == null) {
      this.model.min = this.model.min === null ? this.minValue : this.value.min;
      this.model.max = this.model.max === null ? this.maxValue : this.value.max;
    } else {
      this.model.min = this.model.min === null ? this.minValue : this.data.indexOf(this.value.min);
      this.model.max = this.model.max === null ? this.maxValue : this.data.indexOf(this.value.max);
    }

    if (this.model.max > this.maxValue) this.model.max = this.maxValue;
    if (this.model.min < this.minValue) this.model.min = this.minValue;

    this.curMinRatio = (this.model.min - this.minValue) / (this.maxValue - this.minValue);
    this.curMaxRatio = (this.model.max - this.minValue) / (this.maxValue - this.minValue);
  },
  methods: {
    __mouseDown(e) {
      if (isServer()) return;
      if (!this.disable) {
        if (e.target.classList.contains('range__thumb-min')) {
          this.dragType = dragType.MIN;
        }

        if (e.target.classList.contains('range__thumb-max')) {
          this.dragType = dragType.MAX;
        }

        this.dragg = true;
        document.documentElement.style.cursor = 'grabbing';
        this.dragging = this.__getDragging(e);
        this.__updatePosition(e);
        this.__updateValue();
        this.active = true;
        document.addEventListener('mousemove', this.__mouseMove);
        document.addEventListener('mouseup', this.__mouseUp);
        this.$emit('pan', 'start');
      }
    },
    __mouseUp(e) {
      if (isServer()) return;
      if (this.dragging !== void 0) {
        this.__updatePosition(e);
        e.touch === true && this.__updateValue(true);
        this.dragging = void 0;
        document.documentElement.style.cursor = 'auto';
        this.__updateValue(true);
        this.$emit('pan', 'end');
      }
      this.dragg = false;
      this.active = false;
      document.removeEventListener('mousemove', this.__mouseMove);
      document.removeEventListener('mouseup', this.__mouseUp);

      this.dragType = null;
    },
    __mouseMove(e) {
      if (this.dragg) {
        this.__updatePosition(e);
        if (!this.lazy) { this.__updateValue(); }
      }
    },
    position(e) {
      if (e.touches && e.touches[0]) {
        e = e.touches[0];
      } else if (e.changedTouches && e.changedTouches[0]) {
        e = e.changedTouches[0];
      } else if (e.targetTouches && e.targetTouches[0]) {
        e = e.targetTouches[0];
      }

      return {
        top: e.clientY,
        left: e.clientX,
      };
    },
    decimals() {
      return (String(this.step).trim('0').split('.')[1] || '').length;
    },
    getRatio(evt, dragging) {
      const pos = this.position(evt);
      return between((pos.left - dragging.left) / dragging.width, 0, 1);
    },
    getModel(ratio, min, max, step, decimals) {
      let model = min + ratio * (max - min);

      if (step > 0) {
        const modulo = (model - min) % step;
        model += (Math.abs(modulo) >= step / 2 ? (modulo < 0 ? -1 : 1) * step : 0) - modulo;
      }

      if (decimals > 0) {
        model = parseFloat(model.toFixed(decimals));
      }

      return between(model, min, max);
    },
    __mobileClick(evt) {
      this.dragg = false;
      if (!this.disable) {
        this.__updatePosition(evt, this.__getDragging(evt));
        this.__updateValue(true);
      }

      this.dragType = null;
    },
    __updateValue(change) {
      const min = this.data == null ? this.model.min : this.data[this.model.min];
      const max = this.data == null ? this.model.max : this.data[this.model.max];

      if (min !== this.value.min || max !== this.value.max) {
        this.$emit('input', { min, max });
      }
      change === true && this.$emit('change', { min, max });
    },
    __getDragging(event) {
      const
        { left, top, width, height } = this.$el.getBoundingClientRect();
      const diff = this.maxValue - this.minValue;

      const dragging = {
        left,
        top,
        width,
        height,
        valueMin: this.model.min,
        valueMax: this.model.max,
        ratioMin: (this.model.min - this.minValue) / diff,
        ratioMax: (this.model.max - this.minValue) / diff,
      };

      // const ratio = this.getRatio(event, dragging);
      // let type;

      // if (ratio < dragging.ratioMin) {
      //   type = dragType.MIN;
      // }
      // else if (ratio < dragging.ratioMax) {
      //   type = dragging.ratioMax - ratio < ratio - dragging.ratioMin
      //     ? dragType.MAX
      //     : dragType.MIN;
      // }
      // else {
      //   type = dragType.MAX;
      // }

      // dragging.type = type;
      this.__nextFocus = void 0;

      return dragging;
    },
    __updatePosition(event, dragging = this.dragging) {
      if (this.dragType === null) return;

      const
        ratio = this.getRatio(event, dragging);
      const model = this.getModel(ratio, this.minValue, this.maxValue, this.step, this.decimals);
      let pos;

      const stepRatio = (1 / this.maxValue) * this.step;

      // switch (dragging.type) {
      switch (this.dragType) {
        case dragType.MIN:
          if (ratio + stepRatio >= 1) return;

          if (ratio + stepRatio < dragging.ratioMax) {
            pos = {
              minR: ratio,
              maxR: dragging.ratioMax,
              min: model,
              max: dragging.valueMax,
            };
          } else {
            // pos = {
            //   minR: dragging.ratioMax,
            //   maxR: ratio,
            //   min: dragging.valueMax,
            //   max: model
            // };
            // this.__nextFocus = 'max';

            pos = {
              minR: ratio,
              maxR: ratio + stepRatio,
              min: model,
              max: model + this.step,
            };
          }
          this.__nextFocus = 'min';
          break;

        case dragType.MAX:
          if (ratio - stepRatio <= 0) return;

          if (ratio - stepRatio > dragging.ratioMin) {
            pos = {
              minR: dragging.ratioMin,
              maxR: ratio,
              min: dragging.valueMin,
              max: model,
            };
          } else {
            // pos = {
            //   minR: ratio,
            //   maxR: dragging.ratioMin,
            //   min: model,
            //   max: dragging.valueMin
            // };
            // this.__nextFocus = 'min';

            pos = {
              minR: ratio - stepRatio,
              maxR: ratio,
              min: model - this.step,
              max: model,
            };
          }
          this.__nextFocus = 'max';
          break;
      }

      this.model = {
        min: pos.min,
        max: pos.max,
      };

      if (this.model.min === null || this.model.max === null) {
        this.model.min = pos.min || this.minValue;
        this.model.max = pos.max || this.maxValue;
      }

      this.curMinRatio = pos.minR;
      this.curMaxRatio = pos.maxR;
    },

    arrowHandlerFunction(event, keyCode) {
      if (event.target.classList.contains('range__thumb-min') && keyCode === 37) {
        this.model.min -= this.$props.step;
      }

      if (event.target.classList.contains('range__thumb-min') && keyCode === 39) {
        this.model.min += this.$props.step;
        if (this.data) {
          if (this.model.min > this.data.length - 2) {
            this.model.min = this.data.length - 2;
          }
        }

        if (this.model.min >= this.model.max) {
          this.model.max += this.$props.step;
          this.model.min = this.model.max - this.$props.step;
        }

        if (this.model.min >= this.$props.max - this.$props.step) {
          this.model.min = this.$props.max - this.$props.step;
        }
      }

      if (event.target.classList.contains('range__thumb-max') && keyCode === 37) {
        this.model.max -= this.$props.step;
        if (this.model.max <= this.model.min) {
          this.model.min -= this.$props.step;
          this.model.max = this.model.min + this.$props.step;
        }
        if (this.model.max <= this.minValue + this.$props.step) {
          this.model.max = this.minValue + this.$props.step;
        }
      }

      if (event.target.classList.contains('range__thumb-max') && keyCode === 39) {
        this.model.max += this.$props.step;

        if (this.data) {
          if (this.model.max > this.data.length - 1) {
            this.model.max = this.data.length - 1;
          }
        }
      }

      if (this.model.min < this.minValue) this.model.min = this.minValue;
      if (this.model.max > this.maxValue) this.model.max = this.maxValue;

      this.__debounceUpdateValue(this);
    },

    __debounceUpdateValue: debounce((vm) => {
      vm.__updateValue(true);
    }, 150),

    arrowHandler(event) {
      this.arrowHandlerFunction(event, event.keyCode);
    },
  },
};
